/**
 * Die Morgenzeitung im Ton einer Tageszeitung der Zwischenkriegszeit: Schlagzeilen in gemischter
 * Schreibung mit Ausrufezeichen für die großen Siege, Beschönigungen für die Rückschläge
 * („Frontbegradigung“), die Spitzmarke in Versalien darüber. Titel nach Stadt oder Land:
 * „Dornheimer Tageblatt“, „Varnischer Kurier“, „Ostmarker Rundschau“.
 */
import type { UnitType } from '@friction/engine';
import type { news as en } from '../en/news';
import { adj, adjCap, town as germanTown, nation, theatre, townAdjective, windWord } from '../grammar/de';
import type { NationRef } from '../names';
import type { Translated } from '../types';

const adjOr = (a: string | null, ending: string): string => (a ? adj(a, ending) : `feindlich${ending}`);
const kind = (size: number): string => (size === 3 ? 'Großstadt' : size === 2 ? 'Stadt' : 'Kleinstadt');
const nationDat = (n: NationRef | null): string => (n ? nation(n).the('dat') : 'dem Feind');
const capFirst = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
/** „Dornheimer …“ — Städte ohne Adjektivform (Ziffern) erscheinen als „… von Novo 2“. */
const town = (capital: string, title: string): string => {
  const name = germanTown(capital);
  const a = townAdjective(name);
  return a ? `${a} ${title}` : `${title} von ${name}`;
};

const RAISED: Record<UnitType, string> = {
  infantry: 'Neue Infanteriedivision aufgestellt',
  motorized: 'Neue motorisierte Division aufgestellt',
  armor: 'Neue Panzerdivision aufgestellt',
  artillery: 'Neue Artilleriedivision aufgestellt',
  recon: 'Neue Kavalleriedivision aufgestellt',
  hq: 'Neues Armeeoberkommando gebildet',
  decoy: 'Neuer Verband aufgestellt',
};

export const news: Translated<typeof en> = {
  'news.kicker.front': 'VON DER FRONT',
  'news.kicker.special': 'EXTRABLATT',
  'news.kicker.war': 'DER KRIEG',
  'news.kicker.abroad': 'AUSLAND',
  'news.kicker.science': 'WISSENSCHAFT & WAFFEN',
  'news.kicker.curiosities': 'KURIOSES',
  'news.kicker.intel': 'FEINDNACHRICHTEN',
  'news.kicker.home': 'HEIMATFRONT',
  'news.kicker.weather': 'WETTER',

  'news.war.head': 'Krieg!',
  'news.war': ({ theatre: name }) =>
    `${capFirst(theatre(name).inside)} sind die Feindseligkeiten ausgebrochen. Das Heer marschiert; die Nation steht hinter ihm.`,
  'news.capitulates.head': ({ nation: n }) =>
    n
      ? `${nation(n).bare} ${nation(n).gender === 'pl' ? 'kapitulieren' : 'kapituliert'}`
      : 'Der Feind kapituliert',
  'news.capitulates.ally': ({ by }) =>
    `Unser Verbündeter hat vor ${nationDat(by)} die Waffen gestreckt. An jener Front kämpfen wir nun allein.`,
  'news.capitulates.enemy': ({ byUs, adj: a }) =>
    `Die Regierung hat sich ${byUs ? 'unseren' : `den ${adjOr(a, 'en')}`} Truppen ergeben. Dort schweigen nun die Waffen.`,
  'news.cityOurs.1': ({ city }) => `${city} in unserer Hand!`,
  'news.cityOurs.2': ({ city }) => `Unsere Truppen ziehen in ${city} ein`,
  'news.cityOurs.3': ({ city }) => `Unsere Fahne weht über ${city}`,
  'news.cityOurs.capital': ({ adj: a }) =>
    `Die ${adjOr(a, 'e')} Hauptstadt ist gefallen! Im ganzen Land läuten die Glocken.`,
  'news.cityOurs.town': ({ adj: a, size }) =>
    `Die ${adjOr(a, 'e')} Besatzung ist aus der ${kind(size)} vertrieben. Die Menschen säumen die Straßen.`,
  'news.cityLost.1': ({ city }) => `Frontbegradigung bei ${city}`,
  'news.cityLost.2': ({ city }) => `Planmäßige Absetzbewegung bei ${city}`,
  'news.cityLost.3': ({ city }) => `${city}: Truppen gruppieren um`,
  'news.cityLost.capital':
    'Die Regierung hat sicherere Quartiere bezogen. Die Hauptstadt, so das Ministerium, wird zurückerobert.',
  'news.cityLost.town':
    'Das Kriegsministerium spricht von einer planmäßigen Verlegung der Front in stärkere Stellungen.',
  'news.cityAbroad.head': ({ city, adj: a }) => `${city} in ${adjOr(a, 'er')} Hand`,
  'news.cityAbroad': ({ size, from, to }) =>
    `Die ${kind(size)} wechselt in den Kämpfen zwischen ${nationDat(from)} und ${nationDat(to)} den Besitzer.`,
  'news.hqOurs.head': 'Stab bezieht neues Quartier',
  'news.hqOurs':
    'Ein Hauptquartier hat unter schwierigen Umständen den Standort gewechselt. Befehle könnten eine Zeit lang langsamer gehen.',
  'news.hqTheirs.head': 'Feindlicher Stab zersprengt',
  'news.hqTheirs': ({ adj: a }) =>
    `Ein ${adjOr(a, 'es')} Hauptquartier ist überrannt worden. Seine Karten sollen in unserer Hand sein.`,
  'news.tech.head': ({ tech }) => `${tech} wird eingeführt`,
  'news.decoy.head': 'Feindliche „Armee“ aus Zeltbahn',
  'news.decoy': 'Unsere Spähtrupps fanden {at} eine ganze Division aus Sperrholz und Farbe.',
  'news.stand.head': ({ at }) => `Heldenhafter Widerstand ${at}`,
  'news.stand': ({ n, pockets }) =>
    `${n === 1 ? 'Eine unserer Divisionen hält' : `${n} unserer Divisionen halten`}, vom Feind abgeschnitten, ${pockets > 1 ? `in ${pockets} Kesseln` : 'im Kessel'} aus. Entsatz soll unterwegs sein.`,
  'news.trapped.head': ({ n, adj: a, at }) =>
    `${n} ${adjOr(a, 'e')} ${n === 1 ? 'Division' : 'Divisionen'} ${at} eingeschlossen`,
  'news.trapped': ({ pockets }) =>
    `${pockets > 1 ? 'Die Ringe sind' : 'Der Ring ist'} geschlossen. Unsere Truppen halten die Einschließung, während der Hunger sein Werk tut.`,
  'news.couriers.head': ({ n }) =>
    n === 1 ? 'Feindlicher Meldereiter gefangen' : `${n} feindliche Kuriere gefangen`,
  'news.couriers': 'Die Befehle aus ihren Satteltaschen sind auf dem Weg zum Generalstab.',
  'news.withheld.head': 'Ein Bericht zurückgehalten',
  'news.withheld':
    'Ein Bericht über die Arbeit unseres Nachrichtendienstes wurde auf Wunsch des Zensors entfernt.',
  'news.enemyLost.prisoners': 'Tausende Gefangene eingebracht',
  'news.enemyLost.one': ({ at }) => `Feindliche Division ${at} vernichtet`,
  'news.enemyLost.many': '{n} feindliche Divisionen vernichtet',
  'news.enemyLost': ({ n, unit, surrendered }) =>
    `${n === 1 ? 'Die' : 'Unter ihnen die'} ${unit} ${surrendered ? 'hat die Waffen gestreckt' : 'besteht nicht mehr'}. Die Verluste des Feindes sind schwer.`,
  'news.ownLost.one': 'Eine Division gibt ihr Letztes',
  'news.ownLost.many': 'Schwere Kämpfe — die Nation trauert',
  'news.ownLost': 'Die {unit} hat {at} bis zum Letzten gekämpft. Sie wird neu erstehen.',
  'news.won.head': ({ at }) => `Siegreiche Gefechte ${at}`,
  'news.won': ({ won, lost }) =>
    `${won === 1 ? 'Ein Gefecht' : `${won} Gefechte`} zu unseren Gunsten entschieden${lost ? `, ${lost} noch in schwerem Ringen` : ''}. Die Stimmung der Truppe ist ausgezeichnet.`,
  'news.lostBattles.head': ({ at }) => `Harte Kämpfe ${at}`,
  'news.lostBattles': ({ n }) =>
    `Unsere Truppen stellen sich dem Feind mit Entschlossenheit. ${n === 1 ? 'Eine Stellung wechselte' : `${n} Stellungen wechselten`} den Besitzer.`,
  'news.flares.head': ({ at }) => `Kämpfe flammen ${at} auf`,
  'news.flares': ({ n }) =>
    `${n === 1 ? 'Ein Gefecht' : `${n} Gefechte`} gemeldet. Unsere Berichterstatter sind unterwegs.`,
  'news.raised.one': ({ type }) => RAISED[type],
  'news.raised.many': '{n} neue Divisionen aufgestellt',
  'news.raised': ({ city }) =>
    city
      ? `Vor den Meldestellen in ${city} stehen die Freiwilligen Schlange.`
      : 'Vor den Meldestellen stehen die Freiwilligen Schlange.',
  'news.clockOurs.head': 'Der Sieg ist nahe!',
  'news.clockOurs':
    'Unsere Armeen halten die wichtigsten Städte des Landes. Noch {h} Stunden, und der Krieg ist gewonnen.',
  'news.clockTheirs.head': 'Schwere Stunden für die Nation',
  'news.clockTheirs':
    'Der Feind hält den größeren Teil der wichtigen Städte. In {h} Stunden kann es zu spät sein.',

  'news.quiet.1.head': 'Ruhige Nacht an der Front',
  'news.quiet.1.text':
    'Die Spähtrupps melden keine Veränderung. Im Hauptquartier brannten die Lampen bis zum Morgen.',
  'news.quiet.2.head': 'Kriegsministerium: „Alles in Ordnung“',
  'news.quiet.2.text': 'Ein Sprecher wollte Gerüchte über eine bevorstehende Offensive nicht kommentieren.',
  'news.quiet.3.head': 'Regen auf den Straßen',
  'news.quiet.3.text': 'Die Nachschubkolonnen mühen sich durch den Schlamm; die Kavallerie soll murren.',
  'news.quiet.4.head': 'Ernte hinter der Front eingebracht',
  'news.quiet.4.text': 'Die Bauern bestellen die Felder unter den Augen der Reservebataillone.',
  'news.quiet.5.head': 'Der Stab studiert die Karten',
  'news.quiet.5.text': 'Offiziere brüten über der Lagekarte. „Jeder Befehl braucht Zeit“, bemerkt einer.',
  'news.filler.1.kicker': 'HEIMATFRONT',
  'news.filler.1.head': 'Brotpreise stabil',
  'news.filler.1.text': 'Kohle und Brot unverändert; in der Hauptstadt bleibt der Tabak knapp.',
  'news.filler.2.kicker': 'BEKANNTMACHUNGEN',
  'news.filler.2.head': 'Verdunkelung ab 20 Uhr',
  'news.filler.2.text': 'Die Bevölkerung wird erinnert, dass die Verdunkelungsvorschriften weiter gelten.',
  'news.filler.3.kicker': 'FELDPOST',
  'news.filler.3.head': 'Feldpost weiter gebührenfrei',
  'news.filler.3.text': 'Angehörige dürfen ohne Marke an die Front schreiben. Man halte die Briefe heiter.',
  'news.filler.4.kicker': 'KULTUR',
  'news.filler.4.head': 'Die Oper spielt weiter',
  'news.filler.4.text': 'Die Nationaloper eröffnet ihre Spielzeit, Krieg hin oder her.',
  'news.filler.5.kicker': 'KIRCHEN',
  'news.filler.5.head': 'Glocken im Morgengrauen',
  'news.filler.5.text': 'Jeden Morgen läuten die Glocken für das Heer im Felde.',

  'news.wx.temps': '{min} bis {max}',
  'news.wx.frost.head': 'Strenger Frost hält an',
  'news.wx.frost': 'Die Flüsse sind fest zugefroren; die Schlitten fahren zur Front. {temps}.',
  'news.wx.mud.head': 'Schlamm auf allen Wegen',
  'news.wx.mud': 'Die Felder sind grundlos; die Lastwagen warten auf den Frost. {temps}.',
  'news.wx.blizzard.head': 'Schneesturmwarnung',
  'news.wx.storms.head': 'Unwetter ziehen auf',
  'news.wx.storm': ({ dir, temps }) => `Wind aus ${windWord(dir)}; ${temps}. Die offenen Straßen meiden.`,
  'news.wx.snow.head': 'Schnee erwartet',
  'news.wx.rain.head': 'Regen breitet sich aus',
  'news.wx.rain': ({ dir, temps }) => `Wind aus ${windWord(dir)}; ${temps}. Die Wege werden weich.`,
  'news.wx.fair.head': ({ dir }) => `Heiter, Wind aus ${windWord(dir)}`,
  'news.wx.fair': 'Gute Sicht für die Beobachter; {temps}.',

  'news.masthead.1': ({ capital }) => town(capital, 'Morgenpost'),
  'news.masthead.2': ({ adj: a }) => `${adjCap(a, 'er')} Herold`,
  'news.masthead.3': ({ adj: a }) => `${adjCap(a, 'er')} Kurier`,
  'news.masthead.4': ({ capital }) => town(capital, 'Anzeiger'),
  'news.masthead.5': ({ adj: a }) => `${adjCap(a, 'e')} Rundschau`,
  'news.masthead.6': ({ capital }) => town(capital, 'Tageblatt'),
  'news.masthead.7': ({ adj: a }) => `${adjCap(a, 'e')} Allgemeine Zeitung`,
  'news.motto.1': 'Vom Zensor genehmigt',
  'news.motto.2': 'Für Heer und Vaterland',
  'news.motto.3': 'Alles, was der Zensor erlaubt',
  'news.motto.4': 'Wahrheit · Pflicht · Heimat',
  'news.est': 'Gegr. {year} · {motto}',
};
